import React from 'react';
import { combineClasses } from '../utils/combineClasses';
import BackLink from './BackLink';
import styles from './PageHeader.module.css';

interface PageHeaderProps {
  /** Nombre funcional de la página (dónde estoy), nunca un saludo. */
  title: string;
  /** Descripción breve de la página, en trato formal (usted). */
  subtitle?: string;
  /** Línea de metadatos libre bajo el subtítulo (ej. código/estado en el IR). */
  meta?: React.ReactNode;
  /** Enlace de retorno contextual (solo cuando hay jerarquía de navegación).
   *  `to` → "Volver a {to}"; `label` → texto completo alternativo. */
  backLink?: { to?: string; label?: string; onClick: () => void };
  /** Acciones de página alineadas a la derecha (botones, enlaces). */
  actions?: React.ReactNode;
  /** Fija el encabezado arriba al hacer scroll (vistas de asistente IR/Evaluador,
   *  pensado para el evaluador adulto mayor: el "Volver" queda siempre visible). */
  sticky?: boolean;
  /** Jerarquía del título: 'page' (inicio, más grande) o 'detail' (un nivel de
   *  profundidad — IR postulación / Evaluador evaluación — más chico, `.uc-h5`). */
  titleLevel?: 'page' | 'detail';
}

// Molde único de encabezado de página, compartido por los 3 roles.
// Anatomía: [back link] → título funcional → subtítulo → meta. El nombre y rol
// del usuario ya viven en el dropdown del avatar del header (no se repiten acá).
// Homologa las 4 copias que existían (IR, Evaluador, Admin, Usuarios); ver
// docs/desviaciones-ui.md para la revisión de tipografía pendiente de UI.
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  meta,
  backLink,
  actions,
  sticky = false,
  titleLevel = 'page',
}) => {
  const isDetail = titleLevel === 'detail';
  return (
    <div
      className={combineClasses(
        styles.headerContainer,
        sticky && styles.sticky,
        isDetail && styles.compact
      )}
    >
      <div className={styles.header}>
        {isDetail ? (
          <>
            {/* Vista de detalle: una línea → título a la izquierda, "Volver" a
                la derecha. Sin subtítulo/meta/usuario/acciones. */}
            <h1 className={combineClasses('uc-h5', styles.titleDetail)}>
              {title}
            </h1>
            {backLink && (
              <BackLink
                to={backLink.to}
                label={backLink.label}
                onClick={backLink.onClick}
              />
            )}
          </>
        ) : (
          <>
            <div className={styles.headerContent}>
              {backLink && (
                <div className={styles.backLinkWrap}>
                  <BackLink
                    to={backLink.to}
                    label={backLink.label}
                    onClick={backLink.onClick}
                  />
                </div>
              )}
              <h1 className={styles.title}>{title}</h1>
              {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
              {meta && <div className={styles.meta}>{meta}</div>}
            </div>
            {actions && <div className={styles.headerActions}>{actions}</div>}
          </>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
